import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, CheckCircle2, ExternalLink, Images,
  Plus, Trash2, Upload, ArrowUp, ArrowDown, Copy,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail, resolveAsset, uploadImage } from "../lib/api";

export default function AdminCarousels() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/carousels");
      setItems(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete carousel "${slug}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/carousels/${slug}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-carousels"
    >
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <Images size={16} strokeWidth={2.5} />
              <span className="label-kicker">Photo carousels</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Build photo galleries.
            </h1>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              Each carousel lives at{" "}
              <code>/gallery/&lt;slug&gt;</code> and can also be dropped inside
              any post or page using <code>[[carousel:&lt;slug&gt;]]</code>.
              Photos open full-size when tapped.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="carousel-new"
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white"
          >
            <Plus size={14} /> New carousel
          </button>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="carousel-error"
          >
            {error}
          </p>
        ) : null}

        {creating ? (
          <CreateForm
            onCancel={() => setCreating(false)}
            onCreated={async () => {
              setCreating(false);
              await refresh();
            }}
            setError={setError}
          />
        ) : null}

        {items === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : items.length === 0 && !creating ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="carousel-empty"
          >
            <p className="font-display text-lg font-bold">No carousels yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Click "New carousel" above to build your first one.
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="carousel-list">
            {items.map((c) => (
              <CarouselEditor
                key={c.slug}
                item={c}
                onSaved={refresh}
                onDelete={() => remove(c.slug)}
                setOuterError={setError}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function CreateForm({ onCancel, onCreated, setError }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const slugify = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

  const slugValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  const submit = async (e) => {
    e.preventDefault();
    if (!slugValid || !name.trim()) return;
    setSaving(true);
    setError("");
    try {
      await api.post("/admin/carousels", { slug, name: name.trim() });
      await onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-md border-2 border-[#0A0F17]/20 bg-[#F8FAFC] p-5 sm:p-6"
      data-testid="carousel-create-form"
    >
      <p className="label-kicker text-[var(--brand-body)]">New carousel</p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">Name</span>
          <input
            autoFocus
            required
            maxLength={160}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slug) setSlug(slugify(e.target.value));
            }}
            data-testid="carousel-create-name"
            placeholder="Away days 2025"
            className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </label>
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">Slug (URL)</span>
          <input
            required
            maxLength={80}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="carousel-create-slug"
            placeholder="away-days-2025"
            className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17] ${
              slug && !slugValid ? "border-[#E63946]" : "border-black/15"
            }`}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-[var(--brand-body)]">
        Will live at <code>/gallery/{slug || "&lt;slug&gt;"}</code>. You can add photos right after.
      </p>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!slugValid || !name.trim() || saving}
          data-testid="carousel-create-submit"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? "Creating…" : "Create carousel"}
        </button>
      </div>
    </form>
  );
}

function CarouselEditor({ item, onSaved, onDelete, setOuterError }) {
  const [form, setForm] = useState(snapshotOf(item));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [shortcodeCopied, setShortcodeCopied] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { setForm(snapshotOf(item)); }, [item]);

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshotOf(item));
  const canSave = dirty && form.name.trim().length > 0;

  const save = async (e) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError("");
    setOuterError("");
    try {
      await api.put(`/admin/carousels/${item.slug}`, {
        name: form.name,
        kicker: form.kicker,
        intro: form.intro,
        images: form.images,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError("");
    const next = [...form.images];
    for (const file of files) {
      try {
        const { url } = await uploadImage(file);
        next.push({
          id: cryptoRandomId(),
          url,
          caption: "",
        });
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
    setForm((f) => ({ ...f, images: next }));
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const updateImage = (idx, patch) => {
    setForm((f) => ({
      ...f,
      images: f.images.map((im, i) => (i === idx ? { ...im, ...patch } : im)),
    }));
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= form.images.length) return;
    const next = [...form.images];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setForm((f) => ({ ...f, images: next }));
  };

  const shortcode = `[[carousel:${item.slug}]]`;
  const copyShortcode = async () => {
    try {
      await navigator.clipboard.writeText(shortcode);
      setShortcodeCopied(true);
      setTimeout(() => setShortcodeCopied(false), 1500);
    } catch { /* clipboard blocked — admin can copy manually */ }
  };

  return (
    <form
      onSubmit={save}
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6"
      data-testid={`carousel-${item.slug}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/5 pb-4">
        <div>
          <p className="font-display text-base font-bold leading-none">
            {form.name || <span className="text-[var(--brand-body)]">(unnamed)</span>}
          </p>
          <p className="mt-1 label-kicker text-[var(--brand-body)]">/gallery/{item.slug}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={`/gallery/${item.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
            data-testid={`carousel-${item.slug}-preview`}
          >
            Preview <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded-md border border-[#E63946]/30 px-2 py-1 label-kicker text-[#E63946] hover:bg-[#E63946]/10"
            data-testid={`carousel-${item.slug}-delete`}
          >
            <Trash2 size={10} /> Delete
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Text
          label="Name"
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v }))}
          maxLength={160}
          testid={`carousel-${item.slug}-name`}
        />
        <Text
          label="Kicker (optional)"
          value={form.kicker}
          onChange={(v) => setForm((f) => ({ ...f, kicker: v }))}
          maxLength={80}
          hint="Tiny label above the title on /gallery/&lt;slug&gt;."
          testid={`carousel-${item.slug}-kicker`}
        />
      </div>

      <div className="mt-5">
        <label className="label-kicker text-[var(--brand-body)]">Intro (optional)</label>
        <textarea
          rows={2}
          value={form.intro}
          onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))}
          maxLength={600}
          data-testid={`carousel-${item.slug}-intro`}
          className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          placeholder="A line or two shown above the carousel."
        />
      </div>

      {/* Shortcode helper */}
      <div className="mt-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-3">
        <p className="label-kicker text-[var(--brand-body)]">Embed in any page / post</p>
        <div className="mt-2 flex items-center gap-2">
          <code
            className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-white px-2 py-1.5 font-mono text-xs"
            data-testid={`carousel-${item.slug}-shortcode`}
          >
            {shortcode}
          </code>
          <button
            type="button"
            onClick={copyShortcode}
            data-testid={`carousel-${item.slug}-shortcode-copy`}
            className="inline-flex items-center gap-1 rounded-md border border-black/15 bg-white px-2 py-1.5 text-xs font-bold uppercase tracking-wider hover:border-[#0A0F17]"
          >
            <Copy size={11} />
            {shortcodeCopied ? "Copied ✓" : "Copy"}
          </button>
        </div>
        <p className="mt-2 text-xs text-[var(--brand-body)]">
          Paste this on its own line into any post or custom page body to drop the carousel inline.
        </p>
      </div>

      {/* Photos */}
      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="label-kicker text-[var(--brand-body)]">
            Photos · {form.images.length}
          </label>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`carousel-${item.slug}-upload`}
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-bold hover:border-[#0A0F17] disabled:opacity-50"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "Uploading…" : "Upload photos"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
            className="hidden"
            data-testid={`carousel-${item.slug}-upload-input`}
          />
        </div>

        {form.images.length === 0 ? (
          <div className="mt-3 rounded-md border border-dashed border-black/15 p-8 text-center text-sm text-[var(--brand-body)]">
            No photos yet. Click "Upload photos" to add some.
          </div>
        ) : (
          <ul
            className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
            data-testid={`carousel-${item.slug}-photos`}
          >
            {form.images.map((img, idx) => (
              <li
                key={img.id || idx}
                className="rounded-md border border-black/10 bg-white p-3"
                data-testid={`carousel-${item.slug}-photo-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md bg-black/5">
                    <img
                      src={resolveAsset(img.url)}
                      alt={img.caption || ""}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <label className="block">
                      <span className="label-kicker text-[var(--brand-body)]">Caption</span>
                      <input
                        type="text"
                        value={img.caption}
                        onChange={(e) => updateImage(idx, { caption: e.target.value })}
                        maxLength={240}
                        data-testid={`carousel-${item.slug}-photo-${idx}-caption`}
                        placeholder="Optional"
                        className="mt-1 block w-full rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
                      />
                    </label>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        aria-label="Move up"
                        data-testid={`carousel-${item.slug}-photo-${idx}-up`}
                        className="rounded-md border border-black/10 p-1 hover:border-[#0A0F17] disabled:opacity-40"
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, 1)}
                        disabled={idx === form.images.length - 1}
                        aria-label="Move down"
                        data-testid={`carousel-${item.slug}-photo-${idx}-down`}
                        className="rounded-md border border-black/10 p-1 hover:border-[#0A0F17] disabled:opacity-40"
                      >
                        <ArrowDown size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        data-testid={`carousel-${item.slug}-photo-${idx}-remove`}
                        className="ml-auto inline-flex items-center gap-1 rounded-md border border-[#E63946]/30 px-2 py-1 text-xs font-bold uppercase tracking-wider text-[#E63946] hover:bg-[#E63946]/10"
                      >
                        <Trash2 size={10} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p
          className="mt-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
          data-testid={`carousel-${item.slug}-edit-error`}
        >
          {error}
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-black/5 pt-4">
        {savedFlash ? (
          <span className="inline-flex items-center gap-1 label-kicker text-[#16A34A]">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
        <button
          type="submit"
          disabled={!canSave || saving}
          data-testid={`carousel-${item.slug}-save`}
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function snapshotOf(item) {
  return {
    name: item.name || "",
    kicker: item.kicker || "",
    intro: item.intro || "",
    images: (item.images || []).map((img) => ({
      id: img.id || cryptoRandomId(),
      url: img.url || "",
      caption: img.caption || "",
    })),
  };
}

function cryptoRandomId() {
  // Crypto.randomUUID lands in modern browsers; fall back to a coarse random
  // so the admin never sees an undefined id key.
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).slice(2);
  }
}

function Text({ label, value, onChange, maxLength, hint, testid }) {
  return (
    <label className="block">
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        data-testid={testid}
        className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
      />
      {hint ? <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p> : null}
    </label>
  );
}
